/**
 * useLandingSLKflowLandingMarketLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait LandingSLKflowLandingMarket
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { emitCascadeEvent, useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, LandingSLEconMarketScene, LandingSLKflowLandingMarketEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';

// ============================================================================
// State Machine
// ============================================================================

export type LandingSLKflowLandingMarketState =
  | 'idle';

export type LandingSLKflowLandingMarketEvent =
  | 'INIT'
  | 'MODE_SELECTED'
  | 'SHIFT_DEMAND'
  | 'SHIFT_SUPPLY'
  | 'SET_PRICE'
  | 'MARKET_UPDATED';

type LandingSLKflowLandingMarketSlot = 'main';
interface LandingSLKflowLandingMarketInternalState {
  machineState: LandingSLKflowLandingMarketState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: LandingSLKflowLandingMarketEvent | null;
  slotWriter: Partial<Record<LandingSLKflowLandingMarketSlot, LandingSLKflowLandingMarketEvent | null>>;
  loading: boolean;
  pendingEvent: LandingSLKflowLandingMarketEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<LandingSLEconMarketScene>;
}

type LandingSLKflowLandingMarketAction =
  | { type: 'EVENT_SUCCESS'; event: LandingSLKflowLandingMarketEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: LandingSLKflowLandingMarketSlot[]; renderClear?: LandingSLKflowLandingMarketSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: LandingSLKflowLandingMarketEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof LandingSLEconMarketScene & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function landingSLKflowLandingMarketReducer(
  state: LandingSLKflowLandingMarketInternalState,
  action: LandingSLKflowLandingMarketAction
): LandingSLKflowLandingMarketInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as LandingSLKflowLandingMarketState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<LandingSLEconMarketScene>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<LandingSLEconMarketScene>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<LandingSLEconMarketScene>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: LandingSLKflowLandingMarketInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface LandingSLKflowLandingMarketConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  animate: boolean;
  demandColor: string;
  demandIntercept: number;
  demandShiftStep: number;
  demandSlope: number;
  equilibriumColor: string;
  gridStep: number;
  height: number;
  initialPrice: number;
  interactive: boolean;
  matchMode: string;
  maxPrice: number;
  maxQuantity: number;
  priceLineColor: string;
  priceStep: number;
  summary: string;
  supplyColor: string;
  supplyIntercept: number;
  supplyShiftStep: number;
  supplySlope: number;
  title: string;
  width: number;
}

const DEFAULT_LANDING_SL_KFLOW_LANDING_MARKET_CONFIG = {
  animate: false,
  demandColor: "series-1",
  demandIntercept: 100,
  demandShiftStep: 10,
  demandSlope: 1,
  equilibriumColor: "highlight",
  gridStep: 10,
  height: 440,
  initialPrice: 50,
  interactive: false,
  matchMode: "",
  maxPrice: 100,
  maxQuantity: 90,
  priceLineColor: "guide",
  priceStep: 1,
  summary: "Shift demand or supply and set a price; see surplus or shortage.",
  supplyColor: "series-2",
  supplyIntercept: 10,
  supplyShiftStep: 10,
  supplySlope: 1,
  title: "Supply & Demand Market",
  width: 640,
} as LandingSLKflowLandingMarketConfig;

export interface LandingSLKflowLandingMarketLogicReturn {
  state: LandingSLKflowLandingMarketState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: LandingSLKflowLandingMarketEvent | null;
  slotWriter: Partial<Record<LandingSLKflowLandingMarketSlot, LandingSLKflowLandingMarketEvent | null>>;
  loading: boolean;
  pendingEvent: LandingSLKflowLandingMarketEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<LandingSLEconMarketScene>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof LandingSLKflowLandingMarketEventPayloadMap]: (payload?: LandingSLKflowLandingMarketEventPayloadMap[K]) => Promise<EventResponse> };
  config: LandingSLKflowLandingMarketConfig;
}

export function useLandingSLKflowLandingMarketLogic(config?: Partial<LandingSLKflowLandingMarketConfig>): LandingSLKflowLandingMarketLogicReturn {
  const mergedConfig: LandingSLKflowLandingMarketConfig = { ...DEFAULT_LANDING_SL_KFLOW_LANDING_MARKET_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/landing-sl-kflow-landing-market', orbitalName: 'LandingSL', traitName: 'LandingSLKflowLandingMarket' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(landingSLKflowLandingMarketReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['LandingSLEconMarketScene'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT' && (true)) {
      // idle → idle (on INIT)
        _fields['active'] = true;
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        _fields['title'] = "Supply & Demand Market";
        fields = { ...(fields ?? {}), 'title': _fields['title'] } as Record<string, EventPayloadValue | undefined>;
        _fields['demandIntercept'] = 100;
        fields = { ...(fields ?? {}), 'demandIntercept': _fields['demandIntercept'] } as Record<string, EventPayloadValue | undefined>;
        _fields['demandSlope'] = 1;
        fields = { ...(fields ?? {}), 'demandSlope': _fields['demandSlope'] } as Record<string, EventPayloadValue | undefined>;
        _fields['supplyIntercept'] = 10;
        fields = { ...(fields ?? {}), 'supplyIntercept': _fields['supplyIntercept'] } as Record<string, EventPayloadValue | undefined>;
        _fields['supplySlope'] = 1;
        fields = { ...(fields ?? {}), 'supplySlope': _fields['supplySlope'] } as Record<string, EventPayloadValue | undefined>;
        _fields['price'] = 50;
        fields = { ...(fields ?? {}), 'price': _fields['price'] } as Record<string, EventPayloadValue | undefined>;
        {

        const eqQ = ((+(fields?.demandIntercept ?? 0) - +(fields?.supplyIntercept ?? 0)) / (+(fields?.demandSlope ?? 0) + +(fields?.supplySlope ?? 0)));

        const eqP = (+(fields?.demandIntercept ?? 0) - (+(fields?.demandSlope ?? 0) * +(eqQ ?? 0)));

        const qd = Math.max((0), (((+(fields?.demandIntercept ?? 0) - +(fields?.price ?? 0)) / +(fields?.demandSlope ?? 0))));

        const qs = Math.max((0), (((+(fields?.price ?? 0) - +(fields?.supplyIntercept ?? 0)) / +(fields?.supplySlope ?? 0))));

        const gapVal = (+(qs ?? 0) - +(qd ?? 0));

        const statusLabel = ((+(gapVal ?? 0) > 0.0001) ? "Surplus" : ((+(gapVal ?? 0) < -0.0001) ? "Shortage" : "Equilibrium"));

        const statusMag = Math.abs(Number(gapVal ?? 0));

        const demandZeroQ = Math.min((90), ((+(fields?.demandIntercept ?? 0) / +(fields?.demandSlope ?? 0))));

        const demandZeroP = Math.max((0), ((+(fields?.demandIntercept ?? 0) - (+(fields?.demandSlope ?? 0) * +(demandZeroQ ?? 0)))));

        const supplyEndQ = 90;

        const supplyEndP = (+(fields?.supplyIntercept ?? 0) + (+(fields?.supplySlope ?? 0) * +(supplyEndQ ?? 0)));

        const marketCurves = [{ label: "Demand" as const, color: "series-1" as const, samples: [{ x: 0, y: fields?.demandIntercept }, { x: demandZeroQ, y: demandZeroP }] }, { label: "Supply" as const, color: "series-2" as const, samples: [{ x: 0, y: fields?.supplyIntercept }, { x: supplyEndQ, y: supplyEndP }] }, { label: "Your Price" as const, color: "guide" as const, samples: [{ x: 0, y: fields?.price }, { x: 90, y: fields?.price }] }];

        const marketPoints = [{ x: eqQ, y: eqP, color: "highlight" as const, radius: 6 }, { x: qd, y: fields?.price, color: "series-1" as const, radius: 5 }, { x: qs, y: fields?.price, color: "series-2" as const, radius: 5 }];

        const labelClampLo = 45;

        const labelClampHi = (640 - 45);

        const eqLabelX = Math.min(Math.max(((24 + ((+(eqQ ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0));

        const eqLabelY = ((440 - (24 + ((+(eqP ?? 0) / 100) * (440 - 48)))) - 14);

        const qdLabelX = (Math.min(Math.max(((24 + ((+(qd ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0)) - 8);

        const qdLabelY = ((440 - (24 + ((+(fields?.price ?? 0) / 100) * (440 - 48)))) + 16);

        const qsLabelX = (Math.min(Math.max(((24 + ((+(qs ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0)) + 8);

        const qsLabelY = qdLabelY;

        const eqLabelVisible = (((+(eqQ ?? 0) >= 0) && (+(eqQ ?? 0) <= 90)) && ((+(eqP ?? 0) >= 0) && (+(eqP ?? 0) <= 100)));

        const qdLabelVisible = ((+(qd ?? 0) >= 0) && (+(qd ?? 0) <= 90));

        const qsLabelVisible = ((+(qs ?? 0) >= 0) && (+(qs ?? 0) <= 90));

        const labelShapes = [...([...((eqLabelVisible ? [{ type: "text" as const, x: eqLabelX, y: eqLabelY, text: "Equilibrium" as const, color: "highlight" as const, textSize: "xs" as const, align: "center" as const }] : [])), ...((qdLabelVisible ? [{ type: "text" as const, x: qdLabelX, y: qdLabelY, text: "Qd" as const, color: "series-1" as const, textSize: "xs" as const, align: "right" as const }] : []))]), ...((qsLabelVisible ? [{ type: "text" as const, x: qsLabelX, y: qsLabelY, text: "Qs" as const, color: "series-2" as const, textSize: "xs" as const, align: "left" as const }] : []))];

        _fields['equilibriumQuantity'] = eqQ;
        fields = { ...(fields ?? {}), 'equilibriumQuantity': _fields['equilibriumQuantity'] } as Record<string, EventPayloadValue | undefined>;
        _fields['equilibriumPrice'] = eqP;
        fields = { ...(fields ?? {}), 'equilibriumPrice': _fields['equilibriumPrice'] } as Record<string, EventPayloadValue | undefined>;
        _fields['qDemanded'] = qd;
        fields = { ...(fields ?? {}), 'qDemanded': _fields['qDemanded'] } as Record<string, EventPayloadValue | undefined>;
        _fields['qSupplied'] = qs;
        fields = { ...(fields ?? {}), 'qSupplied': _fields['qSupplied'] } as Record<string, EventPayloadValue | undefined>;
        _fields['marketStatusLabel'] = statusLabel;
        fields = { ...(fields ?? {}), 'marketStatusLabel': _fields['marketStatusLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['marketStatusMagnitude'] = statusMag;
        fields = { ...(fields ?? {}), 'marketStatusMagnitude': _fields['marketStatusMagnitude'] } as Record<string, EventPayloadValue | undefined>;
        _fields['curves'] = marketCurves;
        fields = { ...(fields ?? {}), 'curves': _fields['curves'] } as Record<string, EventPayloadValue | undefined>;
        _fields['points'] = marketPoints;
        fields = { ...(fields ?? {}), 'points': _fields['points'] } as Record<string, EventPayloadValue | undefined>;
        _fields['labelShapes'] = labelShapes;
        fields = { ...(fields ?? {}), 'labelShapes': _fields['labelShapes'] } as Record<string, EventPayloadValue | undefined>;
        }

      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((!true && !(payloadObj?.mode === "")))) {
      // idle → idle (on MODE_SELECTED)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((payloadObj?.mode === ""))) {
      // idle → idle (on MODE_SELECTED)
        _fields['active'] = true;
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        _fields['title'] = "Supply & Demand Market";
        fields = { ...(fields ?? {}), 'title': _fields['title'] } as Record<string, EventPayloadValue | undefined>;
        _fields['demandIntercept'] = 100;
        fields = { ...(fields ?? {}), 'demandIntercept': _fields['demandIntercept'] } as Record<string, EventPayloadValue | undefined>;
        _fields['demandSlope'] = 1;
        fields = { ...(fields ?? {}), 'demandSlope': _fields['demandSlope'] } as Record<string, EventPayloadValue | undefined>;
        _fields['supplyIntercept'] = 10;
        fields = { ...(fields ?? {}), 'supplyIntercept': _fields['supplyIntercept'] } as Record<string, EventPayloadValue | undefined>;
        _fields['supplySlope'] = 1;
        fields = { ...(fields ?? {}), 'supplySlope': _fields['supplySlope'] } as Record<string, EventPayloadValue | undefined>;
        _fields['price'] = 50;
        fields = { ...(fields ?? {}), 'price': _fields['price'] } as Record<string, EventPayloadValue | undefined>;
        {

        const eqQ = ((+(fields?.demandIntercept ?? 0) - +(fields?.supplyIntercept ?? 0)) / (+(fields?.demandSlope ?? 0) + +(fields?.supplySlope ?? 0)));

        const eqP = (+(fields?.demandIntercept ?? 0) - (+(fields?.demandSlope ?? 0) * +(eqQ ?? 0)));

        const qd = Math.max((0), (((+(fields?.demandIntercept ?? 0) - +(fields?.price ?? 0)) / +(fields?.demandSlope ?? 0))));

        const qs = Math.max((0), (((+(fields?.price ?? 0) - +(fields?.supplyIntercept ?? 0)) / +(fields?.supplySlope ?? 0))));

        const gapVal = (+(qs ?? 0) - +(qd ?? 0));

        const statusLabel = ((+(gapVal ?? 0) > 0.0001) ? "Surplus" : ((+(gapVal ?? 0) < -0.0001) ? "Shortage" : "Equilibrium"));

        const statusMag = Math.abs(Number(gapVal ?? 0));

        const demandZeroQ = Math.min((90), ((+(fields?.demandIntercept ?? 0) / +(fields?.demandSlope ?? 0))));

        const demandZeroP = Math.max((0), ((+(fields?.demandIntercept ?? 0) - (+(fields?.demandSlope ?? 0) * +(demandZeroQ ?? 0)))));

        const supplyEndQ = 90;

        const supplyEndP = (+(fields?.supplyIntercept ?? 0) + (+(fields?.supplySlope ?? 0) * +(supplyEndQ ?? 0)));

        const marketCurves = [{ label: "Demand" as const, color: "series-1" as const, samples: [{ x: 0, y: fields?.demandIntercept }, { x: demandZeroQ, y: demandZeroP }] }, { label: "Supply" as const, color: "series-2" as const, samples: [{ x: 0, y: fields?.supplyIntercept }, { x: supplyEndQ, y: supplyEndP }] }, { label: "Your Price" as const, color: "guide" as const, samples: [{ x: 0, y: fields?.price }, { x: 90, y: fields?.price }] }];

        const marketPoints = [{ x: eqQ, y: eqP, color: "highlight" as const, radius: 6 }, { x: qd, y: fields?.price, color: "series-1" as const, radius: 5 }, { x: qs, y: fields?.price, color: "series-2" as const, radius: 5 }];

        const labelClampLo = 45;

        const labelClampHi = (640 - 45);

        const eqLabelX = Math.min(Math.max(((24 + ((+(eqQ ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0));

        const eqLabelY = ((440 - (24 + ((+(eqP ?? 0) / 100) * (440 - 48)))) - 14);

        const qdLabelX = (Math.min(Math.max(((24 + ((+(qd ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0)) - 8);

        const qdLabelY = ((440 - (24 + ((+(fields?.price ?? 0) / 100) * (440 - 48)))) + 16);

        const qsLabelX = (Math.min(Math.max(((24 + ((+(qs ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0)) + 8);

        const qsLabelY = qdLabelY;

        const eqLabelVisible = (((+(eqQ ?? 0) >= 0) && (+(eqQ ?? 0) <= 90)) && ((+(eqP ?? 0) >= 0) && (+(eqP ?? 0) <= 100)));

        const qdLabelVisible = ((+(qd ?? 0) >= 0) && (+(qd ?? 0) <= 90));

        const qsLabelVisible = ((+(qs ?? 0) >= 0) && (+(qs ?? 0) <= 90));

        const labelShapes = [...([...((eqLabelVisible ? [{ type: "text" as const, x: eqLabelX, y: eqLabelY, text: "Equilibrium" as const, color: "highlight" as const, textSize: "xs" as const, align: "center" as const }] : [])), ...((qdLabelVisible ? [{ type: "text" as const, x: qdLabelX, y: qdLabelY, text: "Qd" as const, color: "series-1" as const, textSize: "xs" as const, align: "right" as const }] : []))]), ...((qsLabelVisible ? [{ type: "text" as const, x: qsLabelX, y: qsLabelY, text: "Qs" as const, color: "series-2" as const, textSize: "xs" as const, align: "left" as const }] : []))];

        _fields['equilibriumQuantity'] = eqQ;
        fields = { ...(fields ?? {}), 'equilibriumQuantity': _fields['equilibriumQuantity'] } as Record<string, EventPayloadValue | undefined>;
        _fields['equilibriumPrice'] = eqP;
        fields = { ...(fields ?? {}), 'equilibriumPrice': _fields['equilibriumPrice'] } as Record<string, EventPayloadValue | undefined>;
        _fields['qDemanded'] = qd;
        fields = { ...(fields ?? {}), 'qDemanded': _fields['qDemanded'] } as Record<string, EventPayloadValue | undefined>;
        _fields['qSupplied'] = qs;
        fields = { ...(fields ?? {}), 'qSupplied': _fields['qSupplied'] } as Record<string, EventPayloadValue | undefined>;
        _fields['marketStatusLabel'] = statusLabel;
        fields = { ...(fields ?? {}), 'marketStatusLabel': _fields['marketStatusLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['marketStatusMagnitude'] = statusMag;
        fields = { ...(fields ?? {}), 'marketStatusMagnitude': _fields['marketStatusMagnitude'] } as Record<string, EventPayloadValue | undefined>;
        _fields['curves'] = marketCurves;
        fields = { ...(fields ?? {}), 'curves': _fields['curves'] } as Record<string, EventPayloadValue | undefined>;
        _fields['points'] = marketPoints;
        fields = { ...(fields ?? {}), 'points': _fields['points'] } as Record<string, EventPayloadValue | undefined>;
        _fields['labelShapes'] = labelShapes;
        fields = { ...(fields ?? {}), 'labelShapes': _fields['labelShapes'] } as Record<string, EventPayloadValue | undefined>;
        }

      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'SHIFT_DEMAND' && ((fields?.active === true))) {
      // idle → idle (on SHIFT_DEMAND)
        {

        const newDemandIntercept = Math.max(((+(fields?.supplyIntercept ?? 0) + 1)), (Math.min(((100 * 3)), ((+(fields?.demandIntercept ?? 0) + +(payloadObj?.delta ?? 0))))));

        const eqQ = ((+(newDemandIntercept ?? 0) - +(fields?.supplyIntercept ?? 0)) / (+(fields?.demandSlope ?? 0) + +(fields?.supplySlope ?? 0)));

        const eqP = (+(newDemandIntercept ?? 0) - (+(fields?.demandSlope ?? 0) * +(eqQ ?? 0)));

        const qd = Math.max((0), (((+(newDemandIntercept ?? 0) - +(fields?.price ?? 0)) / +(fields?.demandSlope ?? 0))));

        const qs = Math.max((0), (((+(fields?.price ?? 0) - +(fields?.supplyIntercept ?? 0)) / +(fields?.supplySlope ?? 0))));

        const gapVal = (+(qs ?? 0) - +(qd ?? 0));

        const statusLabel = ((+(gapVal ?? 0) > 0.0001) ? "Surplus" : ((+(gapVal ?? 0) < -0.0001) ? "Shortage" : "Equilibrium"));

        const statusMag = Math.abs(Number(gapVal ?? 0));

        const demandZeroQ = Math.min((90), ((+(newDemandIntercept ?? 0) / +(fields?.demandSlope ?? 0))));

        const demandZeroP = Math.max((0), ((+(newDemandIntercept ?? 0) - (+(fields?.demandSlope ?? 0) * +(demandZeroQ ?? 0)))));

        const supplyEndQ = 90;

        const supplyEndP = (+(fields?.supplyIntercept ?? 0) + (+(fields?.supplySlope ?? 0) * +(supplyEndQ ?? 0)));

        const newCurves = [{ label: "Demand" as const, color: "series-1" as const, samples: [{ x: 0, y: newDemandIntercept }, { x: demandZeroQ, y: demandZeroP }] }, { label: "Supply" as const, color: "series-2" as const, samples: [{ x: 0, y: fields?.supplyIntercept }, { x: supplyEndQ, y: supplyEndP }] }, { label: "Your Price" as const, color: "guide" as const, samples: [{ x: 0, y: fields?.price }, { x: 90, y: fields?.price }] }];

        const newPoints = [{ x: eqQ, y: eqP, color: "highlight" as const, radius: 6 }, { x: qd, y: fields?.price, color: "series-1" as const, radius: 5 }, { x: qs, y: fields?.price, color: "series-2" as const, radius: 5 }];

        const labelClampLo = 45;

        const labelClampHi = (640 - 45);

        const eqLabelX = Math.min(Math.max(((24 + ((+(eqQ ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0));

        const eqLabelY = ((440 - (24 + ((+(eqP ?? 0) / 100) * (440 - 48)))) - 14);

        const qdLabelX = (Math.min(Math.max(((24 + ((+(qd ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0)) - 8);

        const qdLabelY = ((440 - (24 + ((+(fields?.price ?? 0) / 100) * (440 - 48)))) + 16);

        const qsLabelX = (Math.min(Math.max(((24 + ((+(qs ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0)) + 8);

        const qsLabelY = qdLabelY;

        const eqLabelVisible = (((+(eqQ ?? 0) >= 0) && (+(eqQ ?? 0) <= 90)) && ((+(eqP ?? 0) >= 0) && (+(eqP ?? 0) <= 100)));

        const qdLabelVisible = ((+(qd ?? 0) >= 0) && (+(qd ?? 0) <= 90));

        const qsLabelVisible = ((+(qs ?? 0) >= 0) && (+(qs ?? 0) <= 90));

        const labelShapes = [...([...((eqLabelVisible ? [{ type: "text" as const, x: eqLabelX, y: eqLabelY, text: "Equilibrium" as const, color: "highlight" as const, textSize: "xs" as const, align: "center" as const }] : [])), ...((qdLabelVisible ? [{ type: "text" as const, x: qdLabelX, y: qdLabelY, text: "Qd" as const, color: "series-1" as const, textSize: "xs" as const, align: "right" as const }] : []))]), ...((qsLabelVisible ? [{ type: "text" as const, x: qsLabelX, y: qsLabelY, text: "Qs" as const, color: "series-2" as const, textSize: "xs" as const, align: "left" as const }] : []))];

        _fields['demandIntercept'] = newDemandIntercept;
        fields = { ...(fields ?? {}), 'demandIntercept': _fields['demandIntercept'] } as Record<string, EventPayloadValue | undefined>;
        _fields['equilibriumQuantity'] = eqQ;
        fields = { ...(fields ?? {}), 'equilibriumQuantity': _fields['equilibriumQuantity'] } as Record<string, EventPayloadValue | undefined>;
        _fields['equilibriumPrice'] = eqP;
        fields = { ...(fields ?? {}), 'equilibriumPrice': _fields['equilibriumPrice'] } as Record<string, EventPayloadValue | undefined>;
        _fields['qDemanded'] = qd;
        fields = { ...(fields ?? {}), 'qDemanded': _fields['qDemanded'] } as Record<string, EventPayloadValue | undefined>;
        _fields['qSupplied'] = qs;
        fields = { ...(fields ?? {}), 'qSupplied': _fields['qSupplied'] } as Record<string, EventPayloadValue | undefined>;
        _fields['marketStatusLabel'] = statusLabel;
        fields = { ...(fields ?? {}), 'marketStatusLabel': _fields['marketStatusLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['marketStatusMagnitude'] = statusMag;
        fields = { ...(fields ?? {}), 'marketStatusMagnitude': _fields['marketStatusMagnitude'] } as Record<string, EventPayloadValue | undefined>;
        _fields['curves'] = newCurves;
        fields = { ...(fields ?? {}), 'curves': _fields['curves'] } as Record<string, EventPayloadValue | undefined>;
        _fields['points'] = newPoints;
        fields = { ...(fields ?? {}), 'points': _fields['points'] } as Record<string, EventPayloadValue | undefined>;
        _fields['labelShapes'] = labelShapes;
        fields = { ...(fields ?? {}), 'labelShapes': _fields['labelShapes'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('MARKET_UPDATED', _p, { trait: 'LandingSLKflowLandingMarket' }); emitCascadeEvent(eventBus, 'LandingSL', 'LandingSLKflowLandingMarket', { event: 'MARKET_UPDATED', payload: _p, source: { orbital: 'LandingSL', trait: 'LandingSLKflowLandingMarket' } }); })({ price: fields?.price, qDemanded: fields?.qDemanded, qSupplied: fields?.qSupplied, equilibriumPrice: fields?.equilibriumPrice, equilibriumQuantity: fields?.equilibriumQuantity, status: fields?.marketStatusLabel }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'SHIFT_SUPPLY' && ((fields?.active === true))) {
      // idle → idle (on SHIFT_SUPPLY)
        {

        const newSupplyIntercept = Math.max((0), (Math.min(((+(fields?.demandIntercept ?? 0) - 1)), ((+(fields?.supplyIntercept ?? 0) + +(payloadObj?.delta ?? 0))))));

        const eqQ = ((+(fields?.demandIntercept ?? 0) - +(newSupplyIntercept ?? 0)) / (+(fields?.demandSlope ?? 0) + +(fields?.supplySlope ?? 0)));

        const eqP = (+(fields?.demandIntercept ?? 0) - (+(fields?.demandSlope ?? 0) * +(eqQ ?? 0)));

        const qd = Math.max((0), (((+(fields?.demandIntercept ?? 0) - +(fields?.price ?? 0)) / +(fields?.demandSlope ?? 0))));

        const qs = Math.max((0), (((+(fields?.price ?? 0) - +(newSupplyIntercept ?? 0)) / +(fields?.supplySlope ?? 0))));

        const gapVal = (+(qs ?? 0) - +(qd ?? 0));

        const statusLabel = ((+(gapVal ?? 0) > 0.0001) ? "Surplus" : ((+(gapVal ?? 0) < -0.0001) ? "Shortage" : "Equilibrium"));

        const statusMag = Math.abs(Number(gapVal ?? 0));

        const demandZeroQ = Math.min((90), ((+(fields?.demandIntercept ?? 0) / +(fields?.demandSlope ?? 0))));

        const demandZeroP = Math.max((0), ((+(fields?.demandIntercept ?? 0) - (+(fields?.demandSlope ?? 0) * +(demandZeroQ ?? 0)))));

        const supplyEndQ = 90;

        const supplyEndP = (+(newSupplyIntercept ?? 0) + (+(fields?.supplySlope ?? 0) * +(supplyEndQ ?? 0)));

        const newCurves = [{ label: "Demand" as const, color: "series-1" as const, samples: [{ x: 0, y: fields?.demandIntercept }, { x: demandZeroQ, y: demandZeroP }] }, { label: "Supply" as const, color: "series-2" as const, samples: [{ x: 0, y: newSupplyIntercept }, { x: supplyEndQ, y: supplyEndP }] }, { label: "Your Price" as const, color: "guide" as const, samples: [{ x: 0, y: fields?.price }, { x: 90, y: fields?.price }] }];

        const newPoints = [{ x: eqQ, y: eqP, color: "highlight" as const, radius: 6 }, { x: qd, y: fields?.price, color: "series-1" as const, radius: 5 }, { x: qs, y: fields?.price, color: "series-2" as const, radius: 5 }];

        const labelClampLo = 45;

        const labelClampHi = (640 - 45);

        const eqLabelX = Math.min(Math.max(((24 + ((+(eqQ ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0));

        const eqLabelY = ((440 - (24 + ((+(eqP ?? 0) / 100) * (440 - 48)))) - 14);

        const qdLabelX = (Math.min(Math.max(((24 + ((+(qd ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0)) - 8);

        const qdLabelY = ((440 - (24 + ((+(fields?.price ?? 0) / 100) * (440 - 48)))) + 16);

        const qsLabelX = (Math.min(Math.max(((24 + ((+(qs ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0)) + 8);

        const qsLabelY = qdLabelY;

        const eqLabelVisible = (((+(eqQ ?? 0) >= 0) && (+(eqQ ?? 0) <= 90)) && ((+(eqP ?? 0) >= 0) && (+(eqP ?? 0) <= 100)));

        const qdLabelVisible = ((+(qd ?? 0) >= 0) && (+(qd ?? 0) <= 90));

        const qsLabelVisible = ((+(qs ?? 0) >= 0) && (+(qs ?? 0) <= 90));

        const labelShapes = [...([...((eqLabelVisible ? [{ type: "text" as const, x: eqLabelX, y: eqLabelY, text: "Equilibrium" as const, color: "highlight" as const, textSize: "xs" as const, align: "center" as const }] : [])), ...((qdLabelVisible ? [{ type: "text" as const, x: qdLabelX, y: qdLabelY, text: "Qd" as const, color: "series-1" as const, textSize: "xs" as const, align: "right" as const }] : []))]), ...((qsLabelVisible ? [{ type: "text" as const, x: qsLabelX, y: qsLabelY, text: "Qs" as const, color: "series-2" as const, textSize: "xs" as const, align: "left" as const }] : []))];

        _fields['supplyIntercept'] = newSupplyIntercept;
        fields = { ...(fields ?? {}), 'supplyIntercept': _fields['supplyIntercept'] } as Record<string, EventPayloadValue | undefined>;
        _fields['equilibriumQuantity'] = eqQ;
        fields = { ...(fields ?? {}), 'equilibriumQuantity': _fields['equilibriumQuantity'] } as Record<string, EventPayloadValue | undefined>;
        _fields['equilibriumPrice'] = eqP;
        fields = { ...(fields ?? {}), 'equilibriumPrice': _fields['equilibriumPrice'] } as Record<string, EventPayloadValue | undefined>;
        _fields['qDemanded'] = qd;
        fields = { ...(fields ?? {}), 'qDemanded': _fields['qDemanded'] } as Record<string, EventPayloadValue | undefined>;
        _fields['qSupplied'] = qs;
        fields = { ...(fields ?? {}), 'qSupplied': _fields['qSupplied'] } as Record<string, EventPayloadValue | undefined>;
        _fields['marketStatusLabel'] = statusLabel;
        fields = { ...(fields ?? {}), 'marketStatusLabel': _fields['marketStatusLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['marketStatusMagnitude'] = statusMag;
        fields = { ...(fields ?? {}), 'marketStatusMagnitude': _fields['marketStatusMagnitude'] } as Record<string, EventPayloadValue | undefined>;
        _fields['curves'] = newCurves;
        fields = { ...(fields ?? {}), 'curves': _fields['curves'] } as Record<string, EventPayloadValue | undefined>;
        _fields['points'] = newPoints;
        fields = { ...(fields ?? {}), 'points': _fields['points'] } as Record<string, EventPayloadValue | undefined>;
        _fields['labelShapes'] = labelShapes;
        fields = { ...(fields ?? {}), 'labelShapes': _fields['labelShapes'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('MARKET_UPDATED', _p, { trait: 'LandingSLKflowLandingMarket' }); emitCascadeEvent(eventBus, 'LandingSL', 'LandingSLKflowLandingMarket', { event: 'MARKET_UPDATED', payload: _p, source: { orbital: 'LandingSL', trait: 'LandingSLKflowLandingMarket' } }); })({ price: fields?.price, qDemanded: fields?.qDemanded, qSupplied: fields?.qSupplied, equilibriumPrice: fields?.equilibriumPrice, equilibriumQuantity: fields?.equilibriumQuantity, status: fields?.marketStatusLabel }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'SET_PRICE' && ((fields?.active === true))) {
      // idle → idle (on SET_PRICE)
        {

        const newPrice = Math.min(Math.max(Number(payloadObj?.value ?? 0), (0)), (100));

        const qd = Math.max((0), (((+(fields?.demandIntercept ?? 0) - +(newPrice ?? 0)) / +(fields?.demandSlope ?? 0))));

        const qs = Math.max((0), (((+(newPrice ?? 0) - +(fields?.supplyIntercept ?? 0)) / +(fields?.supplySlope ?? 0))));

        const gapVal = (+(qs ?? 0) - +(qd ?? 0));

        const statusLabel = ((+(gapVal ?? 0) > 0.0001) ? "Surplus" : ((+(gapVal ?? 0) < -0.0001) ? "Shortage" : "Equilibrium"));

        const statusMag = Math.abs(Number(gapVal ?? 0));

        const demandCurve = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)(fields?.curves, (0));

        const supplyCurve = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)(fields?.curves, (1));

        const priceLine = { label: "Your Price" as const, color: "guide" as const, samples: [{ x: 0, y: newPrice }, { x: 90, y: newPrice }] };

        const eqPoint = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)(fields?.points, (0));

        const newCurves = [demandCurve, supplyCurve, priceLine];

        const newPoints = [eqPoint, { x: qd, y: newPrice, color: "series-1" as const, radius: 5 }, { x: qs, y: newPrice, color: "series-2" as const, radius: 5 }];

        const labelClampLo = 45;

        const labelClampHi = (640 - 45);

        const eqLabelX = Math.min(Math.max(((24 + ((+(fields?.equilibriumQuantity ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0));

        const eqLabelY = ((440 - (24 + ((+(fields?.equilibriumPrice ?? 0) / 100) * (440 - 48)))) - 14);

        const qdLabelX = (Math.min(Math.max(((24 + ((+(qd ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0)) - 8);

        const qdLabelY = ((440 - (24 + ((+(newPrice ?? 0) / 100) * (440 - 48)))) + 16);

        const qsLabelX = (Math.min(Math.max(((24 + ((+(qs ?? 0) / 90) * (640 - 48)))), Number(labelClampLo ?? 0)), Number(labelClampHi ?? 0)) + 8);

        const qsLabelY = qdLabelY;

        const eqLabelVisible = (((+(fields?.equilibriumQuantity ?? 0) >= 0) && (+(fields?.equilibriumQuantity ?? 0) <= 90)) && ((+(fields?.equilibriumPrice ?? 0) >= 0) && (+(fields?.equilibriumPrice ?? 0) <= 100)));

        const qdLabelVisible = ((+(qd ?? 0) >= 0) && (+(qd ?? 0) <= 90));

        const qsLabelVisible = ((+(qs ?? 0) >= 0) && (+(qs ?? 0) <= 90));

        const labelShapes = [...([...((eqLabelVisible ? [{ type: "text" as const, x: eqLabelX, y: eqLabelY, text: "Equilibrium" as const, color: "highlight" as const, textSize: "xs" as const, align: "center" as const }] : [])), ...((qdLabelVisible ? [{ type: "text" as const, x: qdLabelX, y: qdLabelY, text: "Qd" as const, color: "series-1" as const, textSize: "xs" as const, align: "right" as const }] : []))]), ...((qsLabelVisible ? [{ type: "text" as const, x: qsLabelX, y: qsLabelY, text: "Qs" as const, color: "series-2" as const, textSize: "xs" as const, align: "left" as const }] : []))];

        _fields['price'] = newPrice;
        fields = { ...(fields ?? {}), 'price': _fields['price'] } as Record<string, EventPayloadValue | undefined>;
        _fields['qDemanded'] = qd;
        fields = { ...(fields ?? {}), 'qDemanded': _fields['qDemanded'] } as Record<string, EventPayloadValue | undefined>;
        _fields['qSupplied'] = qs;
        fields = { ...(fields ?? {}), 'qSupplied': _fields['qSupplied'] } as Record<string, EventPayloadValue | undefined>;
        _fields['marketStatusLabel'] = statusLabel;
        fields = { ...(fields ?? {}), 'marketStatusLabel': _fields['marketStatusLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['marketStatusMagnitude'] = statusMag;
        fields = { ...(fields ?? {}), 'marketStatusMagnitude': _fields['marketStatusMagnitude'] } as Record<string, EventPayloadValue | undefined>;
        _fields['curves'] = newCurves;
        fields = { ...(fields ?? {}), 'curves': _fields['curves'] } as Record<string, EventPayloadValue | undefined>;
        _fields['points'] = newPoints;
        fields = { ...(fields ?? {}), 'points': _fields['points'] } as Record<string, EventPayloadValue | undefined>;
        _fields['labelShapes'] = labelShapes;
        fields = { ...(fields ?? {}), 'labelShapes': _fields['labelShapes'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('MARKET_UPDATED', _p, { trait: 'LandingSLKflowLandingMarket' }); emitCascadeEvent(eventBus, 'LandingSL', 'LandingSLKflowLandingMarket', { event: 'MARKET_UPDATED', payload: _p, source: { orbital: 'LandingSL', trait: 'LandingSLKflowLandingMarket' } }); })({ price: fields?.price, qDemanded: fields?.qDemanded, qSupplied: fields?.qSupplied, equilibriumPrice: fields?.equilibriumPrice, equilibriumQuantity: fields?.equilibriumQuantity, status: fields?.marketStatusLabel }));
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: LandingSLKflowLandingMarketSlot[]; clear: LandingSLKflowLandingMarketSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as LandingSLKflowLandingMarketSlot[], clear: [] as LandingSLKflowLandingMarketSlot[] };
          case 'MODE_SELECTED': return { write: ['main'] as LandingSLKflowLandingMarketSlot[], clear: ['main'] as LandingSLKflowLandingMarketSlot[] };
          case 'SET_PRICE': return { write: ['main'] as LandingSLKflowLandingMarketSlot[], clear: [] as LandingSLKflowLandingMarketSlot[] };
          case 'SHIFT_DEMAND': return { write: ['main'] as LandingSLKflowLandingMarketSlot[], clear: [] as LandingSLKflowLandingMarketSlot[] };
          case 'SHIFT_SUPPLY': return { write: ['main'] as LandingSLKflowLandingMarketSlot[], clear: [] as LandingSLKflowLandingMarketSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'LandingSLEconMarketScene': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as LandingSLKflowLandingMarketEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'LandingSLKflowLandingMarket': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof LandingSLKflowLandingMarketEventPayloadMap]: (payload?: LandingSLKflowLandingMarketEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'MODE_SELECTED': (payload?: LandingSLKflowLandingMarketEventPayloadMap['MODE_SELECTED']) => dispatch('MODE_SELECTED', payload),
    'SHIFT_DEMAND': (payload?: LandingSLKflowLandingMarketEventPayloadMap['SHIFT_DEMAND']) => dispatch('SHIFT_DEMAND', payload),
    'SHIFT_SUPPLY': (payload?: LandingSLKflowLandingMarketEventPayloadMap['SHIFT_SUPPLY']) => dispatch('SHIFT_SUPPLY', payload),
    'SET_PRICE': (payload?: LandingSLKflowLandingMarketEventPayloadMap['SET_PRICE']) => dispatch('SET_PRICE', payload),
    'MARKET_UPDATED': (payload?: LandingSLKflowLandingMarketEventPayloadMap['MARKET_UPDATED']) => dispatch('MARKET_UPDATED', payload),
  };

  useUIEvents(enqueueEvent, 'LandingSL.LandingSLKflowLandingMarket', ['INIT', 'MODE_SELECTED', 'SHIFT_DEMAND', 'SHIFT_SUPPLY', 'SET_PRICE'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('LandingSLKflowLandingMarket', (): TraitStateSnapshot => ({
      traitName: 'LandingSLKflowLandingMarket',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT', 'MODE_SELECTED', 'SHIFT_DEMAND', 'SHIFT_SUPPLY', 'SET_PRICE', 'MARKET_UPDATED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
